import { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { useLocalization } from "@i18n";

import type {
  AdminStructureDetail,
  PatchAdminStructureRiskMappingRequest,
} from "../../api/structures/structures.types";
import { usePatchAdminStructureRiskMappingMutation } from "../../api/structures/structures.mutations";
import {
  createAdminStructureRiskMappingSchema,
  mapDetailToRiskMappingForm,
  type AdminStructureRiskMappingForm,
} from "../../schemas";
import { STRUCTURE_RISK_MAPPING_FORM_ID } from "./structure-modal-form-ids";

const RISK_MAPPING_FIELDS = [
  { name: "wasteType" as const, labelKey: "admin.structures.riskMapping.label.wasteType" },
  {
    name: "downstreamHumanOccupation" as const,
    labelKey: "admin.structures.riskMapping.label.downstreamHumanOccupation",
  },
  {
    name: "downstreamEnvironmentalInterest" as const,
    labelKey: "admin.structures.riskMapping.label.downstreamEnvironmentalInterest",
  },
  {
    name: "downstreamInstallation" as const,
    labelKey: "admin.structures.riskMapping.label.downstreamInstallation",
  },
] as const;

export type AdminStructureRiskMappingTabProps = {
  complexId: string;
  structureId: string;
  canWrite: boolean;
  detail: AdminStructureDetail;
  onSaved: (detail: AdminStructureDetail) => void;
  onSavingChange?: (isSaving: boolean) => void;
};

const AdminStructureRiskMappingTab = ({
  complexId,
  structureId,
  canWrite,
  detail,
  onSaved,
  onSavingChange,
}: AdminStructureRiskMappingTabProps) => {
  const { translate } = useLocalization();
  const patchMutation = usePatchAdminStructureRiskMappingMutation();
  const schema = useMemo(
    () => createAdminStructureRiskMappingSchema(translate),
    [translate],
  );

  const form = useForm<AdminStructureRiskMappingForm>({
    resolver: zodResolver(schema),
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: mapDetailToRiskMappingForm(detail),
  });

  useEffect(() => {
    form.reset(mapDetailToRiskMappingForm(detail));
  }, [detail, form]);

  useEffect(() => {
    onSavingChange?.(patchMutation.isPending);
  }, [onSavingChange, patchMutation.isPending]);

  const onSubmit = async (data: AdminStructureRiskMappingForm) => {
    const saved = await patchMutation.mutateAsync({
      complexId,
      structureId,
      payload: data as PatchAdminStructureRiskMappingRequest,
    });
    onSaved(saved);
    form.reset(mapDetailToRiskMappingForm(saved));
  };

  return (
    <form
      id={STRUCTURE_RISK_MAPPING_FORM_ID}
      noValidate
      onSubmit={form.handleSubmit(onSubmit, () => {
        void form.trigger(undefined, { shouldFocus: true });
      })}
      className="flex flex-col gap-4"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {RISK_MAPPING_FIELDS.map(({ name, labelKey }) => {
          const label = translate(labelKey);
          return (
            <Controller
              key={name}
              control={form.control}
              name={name}
              render={({ field, fieldState }) => (
                <label className="group flex min-w-0 w-full flex-col gap-1">
                  <span className="text-label group-aria-invalid:text-error">
                    {label}
                  </span>
                  <input
                    type="text"
                    aria-label={label}
                    aria-invalid={Boolean(fieldState.error) || undefined}
                    disabled={!canWrite}
                    {...field}
                    className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm shadow-sm disabled:opacity-60 aria-invalid:border-error aria-invalid:ring-[3px] aria-invalid:ring-error/20"
                  />
                  {fieldState.error?.message ? (
                    <span className="text-error text-sm">
                      {fieldState.error.message}
                    </span>
                  ) : null}
                </label>
              )}
            />
          );
        })}
      </div>
    </form>
  );
};

export default AdminStructureRiskMappingTab;
